/**
 * recovr — Tabbed Reasons controller
 *
 * Turns the server-rendered ARIA tablist (sections/tabbed-reasons.liquid) into
 * a working tabbed panel: click / Enter / Space / Arrow keys switch the active
 * pill and cross-fade the matching body panel. Progressive enhancement — with
 * no JS the default panel is already shown and every tab is a real link-less
 * button, so nothing is hidden from the user.
 *
 * Accessibility: WAI-ARIA "Tabs with automatic activation" pattern —
 * roving tabindex, ArrowUp/Down (vertical) + Home/End, aria-selected synced.
 * Motion respects prefers-reduced-motion (instant swap, no fade).
 */
class RecovrTabbedReasons extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tablist || this.tabs.length === 0) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.tablist.addEventListener('click', this.#onClick);
    this.tablist.addEventListener('keydown', this.#onKeydown);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab && this.tabs.includes(tab)) this.#activate(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = this.tabs.indexOf(document.activeElement);
    if (current === -1) return;
    let next = -1;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const tab = this.tabs[next];
    tab.focus();
    this.#activate(tab);
  };

  /** @param {Element} tab */
  #activate(tab) {
    const panelId = tab.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (!panel || tab.getAttribute('aria-selected') === 'true') return;

    // Tabs: roving tabindex + selected state.
    this.tabs.forEach((t) => {
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });

    // Panels: hide the others, reveal the target (with an optional fade).
    this.panels.forEach((p) => {
      if (p === panel) return;
      p.hidden = true;
      p.classList.remove('is-active', 'is-entering');
    });

    if (this.reduceMotion) {
      panel.hidden = false;
      panel.classList.add('is-active');
      return;
    }

    // Fade in: mount hidden→entering, then next frame release to the resting
    // state so the CSS transition runs.
    panel.classList.add('is-entering');
    panel.hidden = false;
    panel.classList.add('is-active');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => panel.classList.remove('is-entering'));
    });
  }
}

if (!customElements.get('recovr-tabbed-reasons')) {
  customElements.define('recovr-tabbed-reasons', RecovrTabbedReasons);
}
